import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import logoUrbnet from '@/assets/images/UrbNet2LOGO.png';

gsap.registerPlugin(ScrollTrigger);

const PoliticaPrivacidadePage = () => {
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(pageRef.current?.children || [],
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out'
        }
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Background gradient */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/8 via-background to-primary/5" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--secondary)/0.14),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.10),transparent_50%)]" />
      </div>

      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div ref={pageRef} className="relative z-10 max-w-4xl mx-auto px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src={logoUrbnet} 
            alt="UrbNet Logo" 
            className="h-20 mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-primary via-secondary to-tertiary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
            URBNET
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-foreground" style={{ fontFamily: 'Urbanist, sans-serif' }}>
            Plataforma de Gestão Urbana Inteligente
          </h2>
          <h3 className="text-xl md:text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
            POLÍTICA DE PRIVACIDADE
          </h3>
          <p className="text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
            Última atualização: Junho de 2026
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              Preâmbulo
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              A Marelli Desenvolvimentos Eireli, inscrita no CNPJ/MF sob o nº 35.856.740/0001-09, responsável pela plataforma UrbNet, tem o compromisso de tratar os dados pessoais de seus usuários com transparência, segurança e respeito à privacidade.
            </p>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Esta Política de Privacidade descreve quais dados coletamos, por que coletamos, como utilizamos, com quem compartilhamos, por quanto tempo retemos e quais direitos o titular possui sobre seus dados — em conformidade com a Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais — LGPD), o Marco Civil da Internet (Lei nº 12.965/2014) e demais normas aplicáveis.
            </p>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Ao utilizar o aplicativo UrbNet, o usuário declara ter lido e compreendido esta Política. Caso não concorde com qualquer disposição aqui prevista, recomendamos que não utilize a plataforma.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              1. Quem somos
            </h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li><strong>Controladora dos dados:</strong> Marelli Desenvolvimentos Eireli</li>
              <li><strong>CNPJ:</strong> 35.856.740/0001-09</li>
              <li><strong>Endereço:</strong> Cachoeiro de Itapemirim, Espírito Santo, Brasil</li>
              <li><strong>E-mail de privacidade:</strong> suporte.privacidade@urbnet.app.br</li>
              <li><strong>Encarregado de Dados (DPO):</strong> Thomas Barbosa Marelli — thomas@marelli.dev.br</li>
            </ul>
            <p className="leading-relaxed text-muted-foreground mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Para fins desta Política, o UrbNet atua como Controlador dos dados dos cidadãos que utilizam o aplicativo. No contexto do módulo UrbNet Gov, o UrbNet atua como Operador, sendo os órgãos públicos contratantes os Controladores dos dados de seus cidadãos.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              2. Quais dados coletamos
            </h2>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">2.1 Dados fornecidos diretamente pelo usuário no cadastro</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Nome completo — para identificação na plataforma;</li>
                  <li>Endereço de e-mail — para comunicação, autenticação e notificações;</li>
                  <li>Número de telefone celular — notificações;</li>
                  <li>Senha — armazenada exclusivamente em formato de hash criptográfico, nunca em texto simples;</li>
                  <li>Cidade de residência — para direcionamento de conteúdo e solicitações ao município correto.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">2.2 Dados fornecidos opcionalmente pelo usuário</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Fotografia de perfil — para personalização visual da conta;</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">2.3 Dados coletados automaticamente durante o uso</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Geolocalização — coletada no momento do registro de uma solicitação, para associar o problema ao local correto no mapa. A localização em tempo real só é acessada com permissão explícita do dispositivo e apenas durante o registro da solicitação;</li>
                  <li>Evidências fotográficas — imagens enviadas pelo usuário como comprovação do problema urbano relatado;</li>
                  <li>Dados de uso da plataforma — telas acessadas, funcionalidades utilizadas, tempo de sessão e frequência de uso, coletados de forma anonimizada para melhoria da experiência;</li>
                  <li>Endereço IP e dados do dispositivo — sistema operacional, versão do aplicativo, modelo do dispositivo, coletados para fins de segurança, diagnóstico técnico e prevenção de fraudes;</li>
                  <li>Tokens de notificação push — para envio de alertas sobre o andamento das solicitações.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">2.4 Dados que NÃO coletamos</h3>
                <p className="leading-relaxed mb-2">O UrbNet expressamente não coleta:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Dados biométricos faciais;</li>
                  <li>Dados financeiros ou bancários;</li>
                  <li>Dados de saúde;</li>
                  <li>Dados de menores de 18 anos sem consentimento expresso do responsável legal;</li>
                  <li>Localização contínua em segundo plano — a geolocalização só é acessada durante o ato de registro de uma ouvidoria.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              3. Por que coletamos — Base Legal e Finalidade
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Todo tratamento de dados realizado pelo UrbNet possui base legal definida na LGPD e finalidade específica. A tabela a seguir descreve cada dado, sua finalidade e a base legal aplicável:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li><strong>Nome e e-mail</strong> → Identificação, autenticação e comunicação com o usuário → Base legal: Execução de contrato (Art. 7º, V, LGPD);</li>
              <li><strong>Telefone celular</strong> → notificações de status → Base legal: Execução de contrato (Art. 7º, V, LGPD);</li>
              <li><strong>Senha (hash)</strong> → Controle de acesso seguro à conta → Base legal: Execução de contrato (Art. 7º, V, LGPD);</li>
              <li><strong>Cidade de residência</strong> → Direcionamento de solicitações ao município correto → Base legal: Execução de contrato (Art. 7º, V, LGPD);</li>
              <li><strong>Geolocalização</strong> → Associar o problema ao local no mapa urbano → Base legal: Consentimento explícito no dispositivo (Art. 7º, I, LGPD);</li>
              <li><strong>Evidências fotográficas</strong> → Validação por IA e encaminhamento à ouvidoria competente → Base legal: Execução de contrato (Art. 7º, V, LGPD);</li>
              <li><strong>Dados de uso (anonimizados)</strong> → Melhoria da plataforma e relatórios estatísticos → Base legal: Legítimo interesse (Art. 7º, IX, LGPD);</li>
              <li><strong>IP e dados do dispositivo</strong> → Segurança, diagnóstico técnico e prevenção de fraudes → Base legal: Legítimo interesse (Art. 7º, IX, LGPD);</li>
              <li><strong>Fotografia de perfil</strong> → Personalização visual da conta → Base legal: Consentimento (Art. 7º, I, LGPD).</li>
            </ul>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              4. Validação por Inteligência Artificial
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet utiliza a API do Google (Gemini) para realizar a validação automática das evidências fotográficas enviadas pelos usuários. Este processo:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>Analisa a imagem para verificar sua pertinência com a categoria da solicitação informada;</li>
              <li>Identifica conteúdo inadequado, ofensivo ou que viole os Termos de Uso;</li>
              <li>Não realiza identificação facial, biometria ou reconhecimento de pessoas;</li>
              <li>Não armazena as imagens nos servidores do Google após o processamento — o resultado é retornado ao UrbNet e a imagem não é retida pela API.</li>
            </ul>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              As imagens validadas são armazenadas nos servidores do UrbNet pelo período descrito na Seção 7. O usuário pode solicitar a exclusão de suas imagens nos termos da Seção 9.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              5. Com quem compartilhamos os dados
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet não vende, aluga nem comercializa dados pessoais de usuários a terceiros para nenhuma finalidade.
            </p>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Os dados poderão ser compartilhados exclusivamente nas seguintes situações:
            </p>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">5.1 Órgãos públicos e ouvidorias verificadas</h3>
                <p className="leading-relaxed">Os dados da solicitação — descrição, categoria, geolocalização e evidência fotográfica — são encaminhados à ouvidoria competente para o atendimento. O nome e contato do solicitante podem ser compartilhados apenas se o usuário autorizar explicitamente o contato pelo órgão público.</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">5.2 Prefeitura Supervisora contratante</h3>
                <p className="leading-relaxed">No âmbito do plano de assinatura vigente, a Prefeitura Supervisora tem acesso aos dados agregados das solicitações de seu município, incluindo relatórios de desempenho. O acesso a dados individuais de cidadãos é restrito às solicitações vinculadas às ouvidorias do município.</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">5.3 Fornecedores de infraestrutura tecnológica</h3>
                <p className="leading-relaxed mb-2">O UrbNet utiliza fornecedores terceiros para operação da infraestrutura da plataforma. Todos os fornecedores são contratados mediante cláusulas contratuais que garantem a proteção dos dados e a conformidade com a LGPD:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Serviços de hospedagem e armazenamento em nuvem — para operação dos servidores da plataforma;</li>
                  <li>Google (Gemini API) — para validação automatizada das evidências fotográficas, conforme Seção 4;</li>
                  <li>Serviços de envio de e-mail — para notificações de status e autenticação;</li>
                  <li>Serviços de push notification — para alertas no dispositivo do usuário.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">5.4 Autoridades e poder público</h3>
                <p className="leading-relaxed">Os dados dos usuários poderão ser fornecidos às autoridades judiciais, policiais, administrativas ou ao Ministério Público exclusivamente mediante ordem judicial, requisição legal formal ou nos casos previstos em lei. O UrbNet não fornece dados voluntariamente a autoridades sem amparo legal.</p>
              </div>
            </div>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              6. Transferência Internacional de Dados
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Ao utilizar serviços de infraestrutura em nuvem e a API do Google para validação por IA, os dados podem ser processados em servidores localizados fora do Brasil. Essa transferência internacional ocorre com as seguintes garantias:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li>Os países ou organizações destinatários adotam nível de proteção de dados adequado, conforme avaliação da Autoridade Nacional de Proteção de Dados (ANPD);</li>
              <li>Os contratos com fornecedores internacionais incluem cláusulas contratuais padrão de proteção de dados compatíveis com a LGPD;</li>
              <li>O processamento de imagens pelo Google Gemini ocorre conforme os Termos de Serviço da API do Google e sua política de privacidade, disponível em https://policies.google.com/privacy.</li>
            </ul>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              7. Por quanto tempo retemos os dados
            </h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li><strong>Dados cadastrais</strong> (nome, e-mail, telefone, senha): retidos enquanto a conta estiver ativa. Após solicitação de exclusão, excluídos em até 30 dias — salvo obrigação legal de retenção;</li>
              <li><strong>Solicitações e evidências fotográficas:</strong> retidas por 5 (cinco) anos após o registro, para fins de auditoria pública, transparência e prestação de contas — prazo compatível com a Lei de Acesso à Informação (Lei nº 12.527/2011);</li>
              <li><strong>Dados de geolocalização:</strong> retidos junto à solicitação pelo mesmo prazo de 5 anos;</li>
              <li><strong>Logs de acesso:</strong> retidos por 6 (seis) meses, conforme exigência do Marco Civil da Internet (Art. 15, Lei nº 12.965/2014);</li>
              <li><strong>Dados de uso anonimizados:</strong> podem ser retidos indefinidamente, pois não permitem a identificação do titular;</li>
              <li><strong>Dados de contas excluídas:</strong> após a exclusão da conta, os dados pessoais identificáveis são anonimizados ou excluídos em até 30 dias — as solicitações registradas permanecem no sistema de forma anonimizada pelo prazo legal de 5 anos.</li>
            </ul>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              8. Segurança dos Dados
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet adota as seguintes medidas técnicas e organizacionais para garantir a segurança dos dados pessoais:
            </p>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">8.1 Medidas técnicas</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Criptografia em trânsito: toda comunicação entre o aplicativo e os servidores utiliza protocolo HTTPS/TLS 1.2 ou superior;</li>
                  <li>Criptografia em repouso: dados sensíveis armazenados nos servidores são criptografados;</li>
                  <li>Senhas: armazenadas exclusivamente em formato de hash com salt (bcrypt ou equivalente) — nunca em texto simples;</li>
                  <li>Autenticação: suporte a autenticação de dois fatores (2FA) disponível para usuários e obrigatória para Gestores do módulo Gov;</li>
                  <li>Controle de acesso: princípio do menor privilégio — cada usuário acessa apenas os dados necessários à sua função;</li>
                  <li>Monitoramento: sistemas de detecção de anomalias e tentativas de acesso não autorizado em operação contínua;</li>
                  <li>Backups: cópias de segurança periódicas com criptografia e armazenamento geograficamente redundante.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">8.2 Medidas organizacionais</h3>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Acesso interno restrito: colaboradores da Marelli Desenvolvimentos Eireli só acessam dados pessoais quando estritamente necessário para manutenção e suporte;</li>
                  <li>Política de confidencialidade: todos os colaboradores com acesso a dados pessoais são vinculados por cláusulas de sigilo;</li>
                  <li>Treinamento: equipe orientada periodicamente sobre boas práticas de segurança e proteção de dados.</li>
                </ul>
              </div>
            </div>
            <p className="leading-relaxed text-muted-foreground mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet se compromete a notificar a Autoridade Nacional de Proteção de Dados (ANPD) e os titulares afetados em caso de incidente de segurança que possa acarretar risco ou dano relevante, no prazo de 72 horas após a ciência do incidente, conforme Art. 48 da LGPD.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              9. Direitos do Titular
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Nos termos dos Arts. 17 a 22 da LGPD, o usuário titular dos dados tem os seguintes direitos, exercíveis a qualquer momento:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li><strong>Confirmação e acesso:</strong> saber se o UrbNet trata seus dados e obter cópia dos dados que mantemos sobre você;</li>
              <li><strong>Correção:</strong> solicitar a correção de dados incompletos, inexatos ou desatualizados;</li>
              <li><strong>Anonimização, bloqueio ou eliminação:</strong> para dados desnecessários, excessivos ou tratados em desconformidade com a LGPD;</li>
              <li><strong>Portabilidade:</strong> receber seus dados em formato estruturado e interoperável, mediante requisição;</li>
              <li><strong>Eliminação:</strong> solicitar a exclusão dos dados tratados com base em consentimento, ressalvadas as hipóteses legais de retenção;</li>
              <li><strong>Informação sobre compartilhamento:</strong> saber com quais entidades públicas ou privadas seus dados foram compartilhados;</li>
              <li><strong>Revogação do consentimento:</strong> revogar o consentimento concedido para tratamentos baseados nessa base legal, sem prejuízo da licitude dos tratamentos anteriores;</li>
              <li><strong>Oposição:</strong> opor-se a tratamentos realizados com base em legítimo interesse, quando em desconformidade com a LGPD;</li>
              <li><strong>Revisão de decisões automatizadas:</strong> solicitar revisão humana de decisões tomadas exclusivamente por sistemas automatizados que afetem seus interesses.</li>
            </ul>
            <div>
              <h3 className="font-semibold text-foreground mb-2">Como exercer seus direitos:</h3>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>E-mail: privacidade@urbnet,app.br — com o assunto 'Direitos LGPD — [sua chave e segredo]';</li>
                <li>Canal no aplicativo: Menu &gt; Privacidade.</li>
              </ul>
            </div>
            <p className="leading-relaxed text-muted-foreground mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet responderá às solicitações em até 15 (quinze) dias úteis. Em casos de maior complexidade, o prazo pode ser estendido por igual período mediante comunicação ao titular.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              10. Cookies e Tecnologias de Rastreamento
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet utiliza tecnologias de rastreamento para garantir o funcionamento adequado da plataforma e melhorar a experiência do usuário:
            </p>
            <div className="space-y-4 text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div>
                <h3 className="font-semibold text-foreground mb-2">10.1 Cookies essenciais</h3>
                <p className="leading-relaxed">Necessários para o funcionamento básico da plataforma — autenticação, segurança de sessão e preferências de idioma. Não podem ser desativados sem comprometer o uso da plataforma.</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">10.2 Cookies analíticos</h3>
                <p className="leading-relaxed">Utilizados de forma anonimizada para entender como os usuários interagem com a plataforma — telas mais acessadas, fluxos de navegação, erros técnicos. Os dados gerados são agregados e não permitem identificação individual. O usuário pode optar por não participar nas configurações de privacidade do aplicativo.</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">10.3 Tokens de notificação push</h3>
                <p className="leading-relaxed">Identificadores de dispositivo utilizados exclusivamente para envio de notificações sobre o andamento das solicitações do usuário. Podem ser revogados a qualquer momento nas configurações de notificação do dispositivo ou do aplicativo.</p>
              </div>
            </div>
            <p className="leading-relaxed text-muted-foreground mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet não utiliza cookies de rastreamento publicitário nem compartilha dados de comportamento com redes de publicidade.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              11. Menores de Idade
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet é destinado a usuários com 14 (quatorze) anos ou mais. Usuários entre 14 e 17 anos podem utilizar a plataforma com consentimento prévio e expresso do pai ou responsável legal, conforme exigido pelo Art. 14 da LGPD e pelo Estatuto da Criança e do Adolescente.
            </p>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet Gestão não coleta dados de menores de 18 anos. Caso identifiquemos que dados de uma criança foram coletados sem consentimento adequado, excluiremos esses dados imediatamente e comunicaremos o responsável legal.
            </p>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Se você é responsável por uma criança e acredita que seus dados foram coletados indevidamente, entre em contato pelo e-mail privacidade@urbnet,app.br.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              12. Links e Integrações Externas
            </h2>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet pode exibir links para sites ou serviços externos — como páginas de prefeituras, portais de transparência ou ouvidorias oficiais. O UrbNet não se responsabiliza pelas práticas de privacidade dessas plataformas externas. Recomendamos que o usuário consulte as políticas de privacidade de cada serviço externo acessado.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              13. Alterações nesta Política
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              O UrbNet reserva-se o direito de atualizar esta Política de Privacidade a qualquer momento, para refletir mudanças na legislação, nas práticas de tratamento de dados ou nas funcionalidades da plataforma.
            </p>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Alterações relevantes serão comunicadas ao usuário com antecedência mínima de 15 (quinze) dias, por meio de notificação no aplicativo e/ou e-mail cadastrado. O uso continuado da plataforma após a entrada em vigor das alterações implica aceite da Política atualizada.
            </p>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              O histórico de versões desta Política estará disponível no aplicativo em Menu &gt; Configurações &gt; Privacidade &gt; Histórico da Política.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              14. Contato e Encarregado de Dados (DPO)
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Para exercer seus direitos, tirar dúvidas, reportar violações ou entrar em contato com nosso Encarregado de Proteção de Dados:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              <li><strong>Encarregado de Dados (DPO):</strong> Nome: Thomas Barbosa Marelli Email: thomas@marelli.dev.br</li>
              <li><strong>E-mail de privacidade:</strong> privacidade@urbnet,app.br</li>
              <li><strong>Canal no aplicativo:</strong> Menu &gt; Configurações &gt; Privacidade</li>
              <li><strong>Endereço postal:</strong> Cachoeiro de Itapemirim, Espírito Santo, Brasil</li>
            </ul>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Caso não esteja satisfeito com nossa resposta, você pode apresentar reclamação diretamente à Autoridade Nacional de Proteção de Dados (ANPD) por meio do portal gov.br/anpd.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              15. Legislação Aplicável e Foro
            </h2>
            <p className="leading-relaxed text-muted-foreground mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Esta Política é regida pelas leis da República Federativa do Brasil, em especial pela Lei nº 13.709/2018 (LGPD) e pela Lei nº 12.965/2014 (Marco Civil da Internet).
            </p>
            <p className="leading-relaxed text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Fica eleito o foro da Comarca de Cachoeiro de Itapemirim, Estado do Espírito Santo, para dirimir quaisquer controvérsias decorrentes desta Política, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
            </p>
          </section>

          <section className="bg-card/50 backdrop-blur-md rounded-2xl p-8 border border-border/40 text-center">
            <p className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Urbanist, sans-serif' }}>
              Marelli Desenvolvimentos Eireli
            </p>
            <p className="text-muted-foreground mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              CNPJ: 35.856.740/0001-09
            </p>
            <p className="text-muted-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
              Cachoeiro de Itapemirim — ES — Brasil
            </p>
            <p className="text-muted-foreground mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              Última atualização: Junho de 2026
            </p>
          </section>
        </div>

        {/* Back button */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 text-lg font-semibold text-white bg-gradient-to-r from-primary to-secondary rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Voltar à Página Inicial
          </button>
        </div>
      </div>
    </div>
  );
};

export default PoliticaPrivacidadePage;
